import type { Bounds, MeshData } from './types';

export function boundsOfPositions(positions: Float32Array): Bounds {
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;

  for (let i = 0; i + 2 < positions.length; i += 3) {
    const x = positions[i];
    const y = positions[i + 1];
    const z = positions[i + 2];
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (z < minZ) minZ = z;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    if (z > maxZ) maxZ = z;
  }

  if (!Number.isFinite(minX)) {
    return { min: [0, 0, 0], max: [0, 0, 0], size: [0, 0, 0], diagonal: 0 };
  }

  const size: [number, number, number] = [maxX - minX, maxY - minY, maxZ - minZ];
  return {
    min: [minX, minY, minZ],
    max: [maxX, maxY, maxZ],
    size,
    diagonal: Math.hypot(size[0], size[1], size[2]),
  };
}

export function boundsOfMesh(mesh: MeshData, onlyReferenced = true): Bounds {
  if (!onlyReferenced) return boundsOfPositions(mesh.positions);

  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;

  const p = mesh.positions;
  for (let i = 0; i < mesh.indices.length; i++) {
    const o = mesh.indices[i] * 3;
    const x = p[o];
    const y = p[o + 1];
    const z = p[o + 2];
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (z < minZ) minZ = z;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    if (z > maxZ) maxZ = z;
  }

  if (!Number.isFinite(minX)) {
    return { min: [0, 0, 0], max: [0, 0, 0], size: [0, 0, 0], diagonal: 0 };
  }

  const size: [number, number, number] = [maxX - minX, maxY - minY, maxZ - minZ];
  return {
    min: [minX, minY, minZ],
    max: [maxX, maxY, maxZ],
    size,
    diagonal: Math.hypot(size[0], size[1], size[2]),
  };
}

/** Automatyczna tolerancja scalania: ułamek przekątnej, ograniczony do rozsądnego zakresu. */
export function autoTolerance(mesh: MeshData): number {
  const diag = boundsOfMesh(mesh).diagonal;
  if (!(diag > 0)) return 1e-4;
  return Math.min(0.05, Math.max(1e-5, diag * 1e-5));
}

/**
 * Scala wierzchołki leżące bliżej niż `tolerance`. Zwraca nową siatkę;
 * zdegenerowane trójkąty (po scaleniu) zostają, żeby pipeline mógł je policzyć.
 */
export function weldMesh(
  mesh: MeshData,
  tolerance: number,
): { mesh: MeshData; merged: number; removedVertices: number } {
  const tol = tolerance > 0 ? tolerance : autoTolerance(mesh);
  const tol2 = tol * tol;
  const source = mesh.positions;
  const sourceCount = Math.floor(source.length / 3);

  const cells = new Map<string, number[]>();
  const outPositions: number[] = [];
  const remap = new Int32Array(sourceCount).fill(-1);

  const cellKey = (ix: number, iy: number, iz: number): string => `${ix},${iy},${iz}`;

  const findNear = (x: number, y: number, z: number): number => {
    const ix = Math.floor(x / tol);
    const iy = Math.floor(y / tol);
    const iz = Math.floor(z / tol);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          const bucket = cells.get(cellKey(ix + dx, iy + dy, iz + dz));
          if (!bucket) continue;
          for (const id of bucket) {
            const o = id * 3;
            const ddx = outPositions[o] - x;
            const ddy = outPositions[o + 1] - y;
            const ddz = outPositions[o + 2] - z;
            if (ddx * ddx + ddy * ddy + ddz * ddz <= tol2) return id;
          }
        }
      }
    }
    return -1;
  };

  let merged = 0;
  for (let i = 0; i < sourceCount; i++) {
    const o = i * 3;
    const x = source[o];
    const y = source[o + 1];
    const z = source[o + 2];
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
      remap[i] = -1;
      continue;
    }

    let id = findNear(x, y, z);
    if (id < 0) {
      id = outPositions.length / 3;
      outPositions.push(x, y, z);
      const key = cellKey(Math.floor(x / tol), Math.floor(y / tol), Math.floor(z / tol));
      const bucket = cells.get(key);
      if (bucket) bucket.push(id);
      else cells.set(key, [id]);
    } else {
      merged++;
    }
    remap[i] = id;
  }

  const newIndices: number[] = [];
  const triCount = Math.floor(mesh.indices.length / 3);
  for (let t = 0; t < triCount; t++) {
    const ia = mesh.indices[t * 3];
    const ib = mesh.indices[t * 3 + 1];
    const ic = mesh.indices[t * 3 + 2];
    const a = remap[ia] ?? -1;
    const b = remap[ib] ?? -1;
    const c = remap[ic] ?? -1;
    if (a < 0 || b < 0 || c < 0) continue;
    newIndices.push(a, b, c);
  }

  const welded: MeshData = {
    positions: new Float32Array(outPositions),
    indices: new Uint32Array(newIndices),
  };

  const uniqueVertices = outPositions.length / 3;
  return {
    mesh: welded,
    merged,
    removedVertices: Math.max(0, sourceCount - uniqueVertices),
  };
}

/** Usuwa z geometrii wierzchołki, do których nie odwołuje się żaden trójkąt. */
export function compactMesh(mesh: MeshData): MeshData {
  const used = new Int32Array(Math.floor(mesh.positions.length / 3)).fill(-1);
  const positions: number[] = [];
  const indices = new Uint32Array(mesh.indices.length);
  let next = 0;

  for (let i = 0; i < mesh.indices.length; i++) {
    const old = mesh.indices[i];
    let id = used[old];
    if (id < 0) {
      id = next++;
      used[old] = id;
      const o = old * 3;
      positions.push(mesh.positions[o], mesh.positions[o + 1], mesh.positions[o + 2]);
    }
    indices[i] = id;
  }

  return { positions: new Float32Array(positions), indices };
}
